// Safe transformation between API and form data

import { Quotation, FormValues, Lead, ProductSummary, AdditionalCharge, Tax, Discount } from './Quotation';
import logger, { Logger } from '../../utils/logger';



/**
 * Safely transforms API response data to form-compatible data structure
 * with proper type checking and fallbacks
 */
export const transformApiToFormData = (apiData: any): Partial<FormValues> => {
  Logger.log('transformApiToFormData: Starting transformation', { apiDataType: typeof apiData });
  
  if (!apiData) {
    Logger.log('transformApiToFormData: No API data provided, returning empty object');
    return {};
  }

  // Log the incoming products data to see its structure
  Logger.log('transformApiToFormData: Incoming products data:', apiData.products);

  // Safe transformation of lead data
  let leadData: Lead | string = apiData.lead;
  if (typeof apiData.lead === 'string') {
    // Lead is just an ID string
    Logger.log('transformApiToFormData: Lead is a string ID:', apiData.lead);
    leadData = apiData.lead;
  } else if (apiData.lead && typeof apiData.lead === 'object') {
    // Ensure lead object has required properties
    Logger.log('transformApiToFormData: Lead is an object:', apiData.lead);
    leadData = {
      _id: apiData.lead._id || '',
      name: apiData.lead.name || 'Unknown Lead',
      phone: apiData.lead.phone || '',
      email: apiData.lead.email || null,
      // Add any other expected properties with fallbacks
    };
  }

  // Safe transformation of products data
  const products: ProductSummary[] = [];
  if (Array.isArray(apiData.products)) {
    Logger.log(`transformApiToFormData: Processing ${apiData.products.length} products`);
    
    apiData.products.forEach((apiProduct: any, index: number) => {
      Logger.log(`transformApiToFormData: Processing product ${index}:`, apiProduct);
      
      const product: ProductSummary = {
        id: apiProduct.id || apiProduct._id || '',
        quantity: Number(apiProduct.quantity) || 1,
        unitPrice: Number(apiProduct.unitPrice) || 0,
        description: apiProduct.description || '',
        // FIXED: Always create the product object with name from the top level
        product: {
          _id: apiProduct.id || '',
          name: apiProduct.name || 'Unknown Product'
        }
      };

      Logger.log(`transformApiToFormData: Transformed product ${index}:`, product);
      products.push(product);
    });
  } else {
    Logger.log('transformApiToFormData: No products array found or not an array');
  }

  // Safe transformation of additional charges
  const additionalCharges: AdditionalCharge[] = [];
  if (Array.isArray(apiData.additionalCharges)) {
    Logger.log(`transformApiToFormData: Processing ${apiData.additionalCharges.length} additional charges`);
    
    apiData.additionalCharges.forEach((apiCharge: any, index: number) => {
      if (apiCharge && typeof apiCharge === 'object') {
        const charge = {
          title: apiCharge.title || '',
          type: apiCharge.type === 'percentage' ? 'rate' : apiCharge.type || 'fixed',
          rate: Number(apiCharge.rate) || 0
        };
        
        Logger.log(`transformApiToFormData: Transformed charge ${index}:`, charge);
        additionalCharges.push(charge);
      }
    });
  } else {
    Logger.log('transformApiToFormData: No additional charges array found or not an array');
  }

  // Safe transformation of taxes
  const taxes: Tax[] = [];
  if (Array.isArray(apiData.taxes)) {
    Logger.log(`transformApiToFormData: Processing ${apiData.taxes.length} taxes`);
    
    apiData.taxes.forEach((apiTax: any, index: number) => {
      if (apiTax && typeof apiTax === 'object') {
        const tax = {
          title: apiTax.title || '',
          rate: Number(apiTax.rate) || 0
        };
        
        Logger.log(`transformApiToFormData: Transformed tax ${index}:`, tax);
        taxes.push(tax);
      }
    });
  } else {
    Logger.log('transformApiToFormData: No taxes array found or not an array');
  }

  // Safe transformation of discount
  let discount: Discount | undefined;
  if (apiData.discount && typeof apiData.discount === 'object') {
    Logger.log('transformApiToFormData: Processing discount:', apiData.discount);
    
    discount = {
      type: apiData.discount.type || 'fixed',
      rate: apiData.discount.rate !== null && apiData.discount.rate !== undefined 
        ? Number(apiData.discount.rate) 
        : null
    };
    
    Logger.log('transformApiToFormData: Transformed discount:', discount);
  } else {
    Logger.log('transformApiToFormData: No discount data found');
  }

  // Build the transformed data object
  const formData: Partial<FormValues> = {
    QuotationId: apiData.quotationId || '',
    _id: apiData._id || '',
    lead: leadData,
    products,
    additionalCharges,
    taxes,
    discount,
    notes: apiData.notes || apiData.note || '',
    createdDate: apiData.createdDate ? new Date(apiData.createdDate) : new Date(),
    receivedAmount: apiData.receivedAmount !== undefined ? Number(apiData.receivedAmount) : undefined,
    receivedPaymentMode: apiData.receivedPaymentMode || ''
  };

  Logger.log('transformApiToFormData: Final transformed data:', formData);
  return formData;
};

/**
 * Safely transforms form data to API-compatible structure for submission
 */
export const transformFormToApiData = (formValues: FormValues, isUpdate: boolean = false): any => {
  logger.log('transformFormToApiData: Starting with form values:', formValues);
  
  // Initialize with required fields that must be present in payload
  const payload: any = {
    // Always include these with defaults to match mobile app behavior
    additionalCharges: [],
    taxes: [],
    discount: {
      type: "fixed",
      rate: 0
    }
  };

  logger.log('transformFormToApiData: Initialized payload with default fields');

  // Handle lead reference
  if (!isUpdate && formValues.lead) {
    payload.lead = typeof formValues.lead === 'object' && formValues.lead._id 
      ? formValues.lead._id 
      : formValues.lead;
    logger.log('transformFormToApiData: Set lead:', payload.lead);
  }

  // Handle quotation ID
  if (!isUpdate && formValues.QuotationId) {
    payload.quotationId = formValues.QuotationId;
    logger.log('transformFormToApiData: Set quotationId:', payload.quotationId);
  }

  // Handle products
  if (Array.isArray(formValues.products) && formValues.products.length > 0) {
    payload.products = formValues.products.map(product => ({
      id: product.id || (product.product?._id ? product.product._id : ''),
      quantity: Number(product.quantity) || 1,
      unitPrice: Number(product.unitPrice) || 0,
      description: product.description || ''
    }));
    logger.log(`transformFormToApiData: Processed ${payload.products.length} products`);
  } else {
    payload.products = [];
    logger.log('transformFormToApiData: No products, setting empty array');
  }

  // Handle additional charges - Override the default empty array only if we have values
  if (Array.isArray(formValues.additionalCharges) && formValues.additionalCharges.length > 0) {
    const validCharges = formValues.additionalCharges
      .filter(charge => charge && charge.title && charge.rate !== null && charge.rate !== undefined)
      .map(charge => ({
        title: charge.title,
        type: charge.type || 'fixed',
        rate: Number(charge.rate)
      }));
    
    if (validCharges.length > 0) {
      payload.additionalCharges = validCharges;
      logger.log(`transformFormToApiData: Processed ${validCharges.length} additional charges`);
    }
  }

  // Handle taxes - Override the default empty array only if we have values
  if (Array.isArray(formValues.taxes) && formValues.taxes.length > 0) {
    const validTaxes = formValues.taxes
      .filter(tax => tax && tax.title && tax.rate !== null && tax.rate !== undefined)
      .map(tax => ({
        title: tax.title,
        rate: Number(tax.rate)
      }));
    
    if (validTaxes.length > 0) {
      payload.taxes = validTaxes;
      logger.log(`transformFormToApiData: Processed ${validTaxes.length} taxes`);
    }
  }

  // Handle discount - Override the default only if we have a valid value
  if (formValues.discount && formValues.discount.rate !== null && formValues.discount.rate !== undefined) {
    payload.discount = {
      type: formValues.discount.type === "rate" ? "rate" : "fixed",
      rate: Number(formValues.discount.rate)
    };
    logger.log('transformFormToApiData: Set discount:', payload.discount);
  }

  // Handle notes
  if (formValues.notes) {
    payload.notes = formValues.notes.trim();
    logger.log('transformFormToApiData: Set notes');
  }

  // Handle payment information
  if (formValues.receivedAmount !== undefined && formValues.receivedAmount !== null) {
    const numAmount = Number(formValues.receivedAmount);
    if (!isNaN(numAmount) && numAmount > 0) {
      payload.receivedAmount = numAmount;
      logger.log('transformFormToApiData: Set receivedAmount:', numAmount);
      
      if (formValues.receivedPaymentMode) {
        payload.receivedPaymentMode = formValues.receivedPaymentMode;
        logger.log('transformFormToApiData: Set receivedPaymentMode');
      }
    }
  }

  logger.log('transformFormToApiData: Final payload:', payload);
  return payload;
}